import React, { useState } from 'react';
import Box from '@material-ui/core/Box';
import CircularProgress from '@material-ui/core/CircularProgress';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';
import ListItemText from '@material-ui/core/ListItemText';
import { HTTPStatusCodes } from 'constants/httpStatusCodes';
import { ENDPOINTS } from 'features/saved-searches/constants';
import { useShowAlert } from 'hooks';
import { useFetchWithStatusV2 } from 'hooks/fetchWithStatus';
import { SECONDARY_API_CLIENT } from 'services/API';
import strings from 'strings';
import { When } from 'UI/components/atoms';
import FPIconButton from 'UI/components/atoms/FPIconButton';
import AutocompleteSelect from 'UI/components/molecules/AutocompleteSelect';
import FPButtonBuilder from 'UI/components/molecules/FPButtonBuilder';
import DecisionDialog from 'UI/components/organisms/DecisionDialog';
import FPDrawer from 'UI/components/templates/FPDrawer/FPDrawer';
import { Endpoints } from 'UI/constants/endpoints';
import { UIStatus } from 'UI/constants/status';
import { SvgSubtractCircle } from 'UI/res';
import { OptionRenderers } from 'UI/utils/renderers';

const COPIES = strings.shared.savedSearches.shareDrawer;

export const ShareSavedSearchDrawer = ({
  onClose = () => {},
  open = true,
  savedSearchId = null,
  savedSearchName = '',
  searchOwner = null
}) => {
  const { showAlert } = useShowAlert();
  const { state: usersWithAccessState, refreshData: refreshUsersWithAccess } = useFetchWithStatusV2(
    {
      apiInstance: SECONDARY_API_CLIENT,
      endpoint: savedSearchId ? ENDPOINTS.usersWithAccess.replace(':id', savedSearchId) : null,
      initialStatus: UIStatus.Default
    }
  );
  const usersWithAccess = usersWithAccessState?.results ?? [];
  const isLoadingUsers = usersWithAccessState.status === UIStatus.Loading;

  const [uiState, setUiState] = useState({
    isDeleteDialogOpen: false,
    isDeleting: false,
    isSaving: false,
    removingUserId: null,
    removingUserName: null
  });

  const getShareEndpoint = userId =>
    ENDPOINTS.shareSavedSearch.replace(':id', savedSearchId).replace(':userId', userId);

  const handleAddUserAccess = async (_, value) => {
    if (!savedSearchId || !value?.id) return;

    const { full_name: fullName, id: userId } = value;
    const isOwner = searchOwner?.id === userId;
    const usersHasAccess = usersWithAccess.some(user => user.id === userId);

    if (isOwner || usersHasAccess) {
      showAlert({
        severity: 'warning',
        title: COPIES.alerts.userAlreadySelected.title,
        body: strings.formatString(COPIES.alerts.userAlreadySelected.body, {
          name: fullName
        })
      });
      return;
    }

    try {
      setUiState(prev => ({ ...prev, isSaving: true }));
      const response = await SECONDARY_API_CLIENT.post(getShareEndpoint(userId));

      if (response.status === HTTPStatusCodes.Ok) {
        await refreshUsersWithAccess();
        showAlert({
          title: COPIES.alerts.share.success,
          autoHideDuration: 5000
        });
      }
    } catch (error) {
      showAlert({
        title: COPIES.alerts.share.error,
        error
      });
    } finally {
      setUiState(prev => ({ ...prev, isSaving: false }));
    }
  };

  const handleRemoveUser = async userId => {
    if (!userId) return;

    try {
      setUiState(prev => ({ ...prev, isDeleting: true }));
      const response = await SECONDARY_API_CLIENT.delete(getShareEndpoint(userId));

      if (response.status === HTTPStatusCodes.NoContent) {
        await refreshUsersWithAccess();

        showAlert({
          title: COPIES.alerts.removeAccess.success,
          autoHideDuration: 5000
        });
      }
    } catch (error) {
      showAlert({
        title: COPIES.alerts.removeAccess.error,
        error
      });
    } finally {
      setUiState(prev => ({
        ...prev,
        isDeleteDialogOpen: false,
        isDeleting: false,
        removingUserId: null,
        removingUserName: null
      }));
    }
  };

  const buttonDefinitions = [
    {
      props: {
        text: strings.shared.ui.close,
        variant: 'outlined',
        onClick: onClose
      },
      GridItemProps: {
        md: 3
      }
    }
  ];

  const handleOpenDeleteDialog = ({ userId, userName }) => {
    if (!userId || !userName) return;

    setUiState(prev => ({
      ...prev,
      isDeleteDialogOpen: true,
      removingUserId: userId,
      removingUserName: userName
    }));
  };

  return (
    <FPDrawer
      CardHeaderProps={{
        title: COPIES.title,
        subheader: savedSearchName
      }}
      FPDrawerProps={{
        open
      }}
      onClose={onClose}
      footer={
        <FPButtonBuilder
          GridContainerProps={{ spacing: 2 }}
          buttonDefinitions={buttonDefinitions}
        />
      }
    >
      <AutocompleteSelect
        disabled={uiState.isSaving || uiState.isDeleting || isLoadingUsers}
        name="selectedRecruiter"
        onSelect={handleAddUserAccess}
        placeholder={COPIES.selectUserPlaceholder}
        renderOption={OptionRenderers.globalSearchUser('full_name')}
        typeahead
        typeaheadLimit={15}
        url={Endpoints.Users}
      />

      <List>
        {searchOwner && (
          <ListItem disableGutters>
            <ListItemText
              primary={searchOwner?.name}
              secondary={COPIES.usersWithAccess.ownerLabel}
            />
          </ListItem>
        )}

        <When condition={isLoadingUsers || uiState.isSaving}>
          <ListItem disableGutters>
            <Box
              alignItems="center"
              display="flex"
              flexDirection="column"
              gridGap={8}
              height="100%"
              justifyContent="center"
              width="100%"
            >
              <CircularProgress />
              {uiState.isSaving && strings.shared.ui.saving}
            </Box>
          </ListItem>
        </When>

        <When condition={!isLoadingUsers && !uiState.isSaving}>
          {usersWithAccess?.map(({ id, name = '' }) => (
            <ListItem key={id} disableGutters>
              <ListItemText primary={name} secondary={COPIES.usersWithAccess.accessLabel} />

              <ListItemSecondaryAction>
                <Box
                  alignItems="center"
                  display="flex"
                  height={32}
                  justifyContent="center"
                  width={32}
                >
                  <FPIconButton
                    disabled={uiState.isDeleting}
                    icon={SvgSubtractCircle}
                    onClick={() => handleOpenDeleteDialog({ userId: id, userName: name })}
                    tooltipProps={{
                      placement: 'bottom',
                      title: COPIES.usersWithAccess.removeAccess
                    }}
                  />
                </Box>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </When>
      </List>

      <When condition={uiState.isDeleteDialogOpen}>
        <DecisionDialog
          fetching={uiState.isDeleting}
          isOpened
          message={strings.formatString(COPIES.removeAccessDialog.message, {
            name: uiState.removingUserName ?? ''
          })}
          onClose={() =>
            setUiState(prev => ({
              ...prev,
              isDeleteDialogOpen: false,
              removingUserId: null,
              removingUserName: null
            }))
          }
          onConfirm={() => handleRemoveUser(uiState.removingUserId)}
          showSeverityIcon
          title={COPIES.removeAccessDialog.title}
          withButtons="YesNo"
        />
      </When>
    </FPDrawer>
  );
};
